'use client';

import { createContext, useContext } from 'react';
import type { LDAppFlags } from 'src/config/LDFeatureFlags';

/**
 * Carries the LaunchDarkly flag set to consumers WITHOUT depending on the SDK.
 *
 * APX-303: `useFeatureFlags` is consumed from nearly every coordinator surface,
 * so importing `useFlags` from `launchdarkly-react-client-sdk` inside it pinned
 * the whole ~240 KB client SDK into the up-front bundle — lazy-loading the
 * provider alone could never have helped while that import existed. This
 * context is the seam that breaks the link: `LDAppFlags` is a local interface
 * (src/config/LDFeatureFlags.ts) and the import above is type-only, so this
 * module compiles to zero SDK references. The SDK now lives only inside
 * `src/app/LaunchDarklyClientBridge.tsx`, which is loaded lazily and pushes the
 * flags it receives in through this provider.
 *
 * The default value is an empty flag set, matching exactly what `useFlags()`
 * returned when no `LDProvider` was mounted above the caller. That keeps the
 * `(public)` island — which renders `Providers` with `includeLaunchDarkly={false}`
 * — resolving flags the same way it did before.
 */
export const EMPTY_LAUNCH_DARKLY_FLAGS: LDAppFlags = {};

export const LaunchDarklyFlagsContext = createContext<LDAppFlags>(EMPTY_LAUNCH_DARKLY_FLAGS);

export const useLaunchDarklyFlags = (): LDAppFlags => useContext(LaunchDarklyFlagsContext);

/**
 * True when the flags came from a server bootstrap, so they are already the org's own
 * during SSR. A context rather than Redux: the store is a module singleton shared across
 * server requests, so it must never be written during render.
 */
export const LaunchDarklyBootstrappedContext = createContext(false);

export const useIsLaunchDarklyBootstrapped = (): boolean =>
  useContext(LaunchDarklyBootstrappedContext);

export default LaunchDarklyFlagsContext;
